import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { HistogramIcon } from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { histogramHintEnabledState } from '~/state/atoms/toolsAtoms';
import { isTextEntryElement } from '~/utils/textEntry';

export default function WindowingHistogramTool() {
    const intl = useIntl();
    const [enabled, setEnabled] = useRecoilState(histogramHintEnabledState);
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;

    // Hold the latest `enabled` so the keydown listener below can read it
    // without being in the effect's dependency list, letting the listener
    // mount once instead of re-subscribing on every toggle.
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    // Single source of truth for both the toolbar click and the `H` hotkey.
    const toggle = useCallback(() => {
        const next = !enabledRef.current;
        enabledRef.current = next;
        setEnabled(next);
    }, [setEnabled]);

    // Plain `H` toggles the histogram. Same guard pattern as the other plain
    // key shortcuts: skip when any modifier is held (so Ctrl+H still routes to
    // sensitive-info toggle), on auto-repeat, when focus is in a text input,
    // or while a tool action is in progress. Only one toolbar step renders
    // at a time, so there is no listener duplication across steps.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                event.shiftKey
            ) {
                return;
            }
            if (event.repeat) {
                return;
            }
            if (event.key.toLowerCase() !== 'h') {
                return;
            }
            if (isTextEntryElement(document.activeElement)) {
                return;
            }
            if (toolManager?.isActiveToolActionInProgress()) {
                return;
            }
            toggle();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [toggle, toolManager]);

    return (
        <ToolbarButton
            icon={<HistogramIcon />}
            tooltip={intl.formatMessage({ id: 'toolbar.histogram.tooltip' })}
            selected={enabled}
            ariaPressed={enabled}
            onClick={toggle}
        />
    );
}
